import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Upload, Loader2, RefreshCw, ShoppingBag, User, ChevronRight, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIN_STATUS = {
  paid: { label: "Pago", cls: "bg-[#E9F5EC] text-[#1E6B33] border-[#C6E4CE]" },
  refunded: { label: "Reembolsado", cls: "bg-[#F0EAFE] text-[#4B2A94] border-[#D9CBF5]" },
  partially_refunded: { label: "Parcial. Reemb.", cls: "bg-[#F0EAFE] text-[#4B2A94] border-[#D9CBF5]" },
  pending: { label: "Pendente", cls: "bg-[#FFF7E6] text-[#8A5A00] border-[#F2E0B8]" },
  voided: { label: "Anulado", cls: "bg-[#FBEAEA] text-[#8F1114] border-[#F0CBCC]" },
  authorized: { label: "Autorizado", cls: "bg-[#EEF3FA] text-[#1F4B8E] border-[#CDDDF2]" },
};

export function FinancialStatusBadge({ status }) {
  const key = (status || "").toLowerCase();
  const cfg = FIN_STATUS[key] || {
    label: status || "—",
    cls: "bg-white text-[var(--ldr-text)] border-[var(--ldr-border-strong)]",
  };
  return (
    <span
      className={`badge-status border ${cfg.cls}`}
      data-testid={`fin-status-${key || "unknown"}`}
    >
      {cfg.label}
    </span>
  );
}

export function fmtMoney(v, currency) {
  if (v === null || v === undefined) return "—";
  try {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: currency || "EUR",
    }).format(v);
  } catch (_e) {
    return `${v}`;
  }
}

export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
}

export default function AdminEncomendas() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ orders: [], customer_summary: null });
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async (term = q) => {
    setLoading(true);
    try {
      const params = {};
      if (term && term.trim()) params.q = term.trim();
      const { data } = await api.get("/admin/encomendas", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    load(q);
  };

  const onImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("Selecione um ficheiro CSV.");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/admin/encomendas/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success("Importação concluída.");
      load("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
    }
  };

  const { orders, customer_summary: summary } = data;

  return (
    <div data-testid="admin-encomendas-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ldr-text)]">Encomendas</h1>
          <p className="text-sm text-[var(--ldr-text-muted)] mt-1">
            Consulta do histórico de encomendas importado da Shopify.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(q)}
            className="ldr-btn-outline"
            data-testid="encomendas-refresh"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="ldr-btn-primary"
            data-testid="encomendas-toggle-import"
          >
            <Upload size={14} /> Importar CSV
          </button>
        </div>
      </div>

      {showImport && (
        <div className="ldr-card p-5 mb-4" data-testid="encomendas-import-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">Importar CSV Shopify</h3>
              <p className="text-sm text-[var(--ldr-text-muted)] mt-1">
                Faça upload do ficheiro <span className="font-mono">orders_export.csv</span> da Shopify.
                A importação é <strong>idempotente</strong> — importar duas vezes o mesmo ficheiro não cria duplicados.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowImport(false)}
              className="text-[var(--ldr-text-muted)] hover:text-[var(--ldr-red)] p-1"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="text-sm"
              data-testid="encomendas-import-file"
            />
            <button
              type="button"
              onClick={onImport}
              disabled={importing}
              className="ldr-btn-primary"
              data-testid="encomendas-import-submit"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {importing ? "A importar…" : "Importar"}
            </button>
          </div>
          {importResult && (
            <div className="mt-4 text-sm bg-[var(--ldr-bg-alt)] border border-[var(--ldr-border)] rounded p-3"
                 data-testid="encomendas-import-result">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <ImportStat label="Importadas" value={importResult.encomendas_importadas} />
                <ImportStat label="Atualizadas" value={importResult.encomendas_atualizadas} />
                <ImportStat label="Itens" value={importResult.itens_importados} />
                <ImportStat label="Linhas ignor." value={importResult.linhas_ignoradas} />
                <ImportStat label="Erros" value={importResult.erros} />
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={onSubmit} className="mb-6" data-testid="encomendas-search-form">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ldr-text-muted)]"
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar por nome, email, telefone ou número da compra"
            className="ldr-input pl-11 !py-3.5 !text-base"
            data-testid="encomendas-search-input"
          />
        </div>
      </form>

      {loading ? (
        <div className="ldr-card p-8 text-center text-[var(--ldr-text-muted)]" data-testid="encomendas-loading">
          A carregar…
        </div>
      ) : orders.length === 0 ? (
        <div className="ldr-card p-12 text-center text-[var(--ldr-text-muted)]" data-testid="encomendas-empty">
          {q ? "Sem resultados para esta pesquisa." : "Ainda não existem encomendas importadas."}
        </div>
      ) : (
        <div>
          {summary && (
            <div className="ldr-card p-5 mb-4" data-testid="encomendas-customer-summary">
              <div className="flex items-center gap-2 text-[var(--ldr-text-muted)] text-xs uppercase tracking-widest mb-2">
                <User size={14} /> Cliente identificado
              </div>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <div className="text-xl font-bold text-[var(--ldr-text)]">
                    {summary.name || "—"}
                  </div>
                  <div className="text-sm text-[var(--ldr-text-muted)] mt-1">
                    {summary.email || "—"} · {summary.phone || "—"}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-right">
                  <SumBox label="Compras" value={summary.total_orders} />
                  <SumBox label="Itens" value={summary.total_items} />
                  <SumBox
                    label="Total pago"
                    value={fmtMoney(summary.total_paid, summary.currency)}
                    strong
                  />
                </div>
              </div>
            </div>
          )}

          <div className="ldr-card overflow-hidden" data-testid="encomendas-results">
            <ul className="divide-y divide-[var(--ldr-border)]">
              {orders.map((o) => (
                <li key={o.name} data-testid={`order-row-${o.name_normalized}`}>
                  <Link
                    to={`/admin/encomendas/${encodeURIComponent(o.name.replace(/^#/, ""))}`}
                    className="flex items-center gap-4 p-4 hover:bg-[var(--ldr-bg-alt)] transition-colors"
                  >
                    <div className="shrink-0 w-10 h-10 rounded-md bg-[#FBEAEA] text-[var(--ldr-red)] flex items-center justify-center">
                      <ShoppingBag size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-bold text-[var(--ldr-text)]">{o.name}</span>
                        <FinancialStatusBadge status={o.financial_status} />
                        <span className="text-xs text-[var(--ldr-text-muted)]">
                          {fmtDate(o.order_created_at)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-[var(--ldr-text-muted)] truncate">
                        {o.customer_name || "—"}
                        {o.email ? <> · {o.email}</> : null}
                      </div>
                    </div>
                    <div className="hidden sm:block text-right">
                      <div className="font-semibold text-[var(--ldr-text)]">
                        {fmtMoney(o.total, o.currency)}
                      </div>
                      <div className="text-xs text-[var(--ldr-text-muted)]">
                        {o.total_items_count || 0} itens
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-[var(--ldr-text-muted)] shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportStat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">{label}</div>
      <div className="font-semibold">{value ?? 0}</div>
    </div>
  );
}

function SumBox({ label, value, strong }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">{label}</div>
      <div className={strong ? "text-lg font-bold text-[var(--ldr-text)]" : "text-lg font-semibold"}>
        {value}
      </div>
    </div>
  );
}
